import type { AssetTypeSelectOptionItem } from "@/modules/asset-type/asset-type-select";

import { useTranslation } from "react-i18next";

import {
  assetHasTechnicalData,
  useAssetEditAssetType,
  type AssetLite,
} from "@/entities/asset";
import { useAssetsBatchUpdate } from "@/entities/assets";
import { useBatchEditForm } from "@/modules/batch-edit-form";
import { isSameQueryableSelectOptionItem } from "@/modules/queryable-select";

import {
  getAssetsBatchEditAssetTypeInitialValue,
  shouldDisableAssetsBatchEditAssetType,
} from "./assets-batch-edit-asset-type.helper";

export const useDisableAssetsBatchEditAssetType = (assets?: AssetLite[]) => {
  const { t } = useTranslation();

  const disabled = shouldDisableAssetsBatchEditAssetType(assets);

  const tooltip = t("asset:batchEdit.assetType.disabledTooltip");

  return {
    disabled,
    tooltip: disabled ? tooltip : undefined,
  };
};

export const useAssetsBatchEditAssetType = (assets: AssetLite[]) => {
  const fieldName = "asset_type_id";

  const initialValue = getAssetsBatchEditAssetTypeInitialValue(assets);

  const { mutate, isPending, status, error } = useAssetsBatchUpdate(assets);

  const onMutateReady = (option: AssetTypeSelectOptionItem) => {
    if (!option) return;
    mutate({ [fieldName]: option.value });
  };

  const formProps = useBatchEditForm<AssetTypeSelectOptionItem>({
    fieldName,
    initialValue,
    isEqualFn: ({ initialValue, formValue }) =>
      isSameQueryableSelectOptionItem(initialValue, formValue),
    onMutateReady,
    status,
    error,
  });

  return {
    ...formProps,
    fieldName,
    initialValue,
    isPending,
  };
};

export const useAssetsBatchEditAssetTypeAlert = (assets: AssetLite[] = []) => {
  const firstAsset = assets[0];

  const assetsWithTechnicalData = assets.filter((asset) =>
    assetHasTechnicalData(asset)
  );

  const { setAssetTypeId, isAssetTypeLoading, showAssetTypeAlert } =
    useAssetEditAssetType(firstAsset?.asset_type);

  return {
    setAssetTypeId,
    isAssetTypeLoading,
    showAssetTypeAlert: assetsWithTechnicalData.length
      ? showAssetTypeAlert
      : false,
    concernedAssets: assetsWithTechnicalData,
  };
};
